import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import FormPage from '../../components/ui/FormPage.tsx';
import api from '../../services/api.ts';
import { handlePtBrSubmit, ptBrValidityProps } from '../../utils/htmlValidation.ts';
import { assertNonNegative } from '../../utils/numberGuards.ts';
import { applyPlateMask, isValidPlate, normalizePlate, PLATE_INVALID_MSG } from '../../utils/licensePlate.ts';

const FrotaNovo: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    licensePlate: '',
    color: '',
    vehicleKind: 'carro',
    fleetStatus: 'active',
    currentKm: 0,
    driverName: '',
    estimatedValue: 0,
    usageLabel: 'Pessoal / família',
    nextPreventiveKm: '' as string | number,
  });
  const [error, setError] = useState('');

  const mutation = useMutation(
    async () => {
      const payload: any = {
        ...form,
        licensePlate: normalizePlate(form.licensePlate),
        year: Number(form.year),
        currentKm: Number(form.currentKm) || 0,
        estimatedValue: Number(form.estimatedValue) || null,
        nextPreventiveKm: form.nextPreventiveKm === '' ? null : Number(form.nextPreventiveKm),
        driverName: form.driverName || null,
      };
      const res = await api.post('/patrimonio/frota', payload);
      return res.data;
    },
    {
      onSuccess: data => navigate(`/patrimonio/frota/${data.id}`),
      onError: (err: any) => {
        setError(err?.response?.data?.error || 'Erro ao criar veículo da frota');
      },
    }
  );

  const set = (key: string, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <FormPage
      backTo="/patrimonio/frota"
      backLabel="Voltar à frota"
      breadcrumb="Patrimônio / Frota / Novo"
      title="Novo veículo da frota"
      subtitle="Cadastro para uso contínuo (km, manutenções, custos). Diferente do estoque para venda."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/patrimonio/frota')}>
            Cancelar
          </button>
          <button type="submit" form="frota-novo-form" className="btn btn-primary" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Salvando…' : 'Salvar veículo'}
          </button>
        </>
      }
    >
      {error && <div className="field-error" style={{ marginBottom: 12 }}>{error}</div>}
      <form
        id="frota-novo-form"
        noValidate
        onSubmit={e =>
          handlePtBrSubmit(e, () => {
            const errs = [
              assertNonNegative(form.currentKm, 'Km atual'),
              assertNonNegative(form.nextPreventiveKm, 'Próx. preventiva (km)'),
              assertNonNegative(form.estimatedValue, 'Valor estimado'),
            ].filter(Boolean);
            if (errs.length) {
              setError(errs[0] as string);
              return;
            }
            if (!isValidPlate(form.licensePlate)) {
              setError(PLATE_INVALID_MSG);
              return;
            }
            setError('');
            mutation.mutate();
          })
        }
      >
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field">
            <label>Marca *</label>
            <input required {...ptBrValidityProps} className="input" value={form.make} onChange={e => set('make', e.target.value)} />
          </div>
          <div className="field">
            <label>Modelo *</label>
            <input required {...ptBrValidityProps} className="input" value={form.model} onChange={e => set('model', e.target.value)} />
          </div>
          <div className="field">
            <label>Ano *</label>
            <input required {...ptBrValidityProps} type="number" className="input" value={form.year} onChange={e => set('year', e.target.value)} />
          </div>
          <div className="field">
            <label>Placa *</label>
            <input required {...ptBrValidityProps} className="input mono" value={form.licensePlate} onChange={e => set('licensePlate', applyPlateMask(e.target.value))} placeholder="ABC-1D23 ou ABC-1234" maxLength={8} />
          </div>
          <div className="field">
            <label>Tipo</label>
            <select className="select" value={form.vehicleKind} onChange={e => set('vehicleKind', e.target.value)}>
              <option value="carro">Carro</option>
              <option value="moto">Moto</option>
              <option value="utilitario">Utilitário</option>
            </select>
          </div>
          <div className="field">
            <label>Status</label>
            <select className="select" value={form.fleetStatus} onChange={e => set('fleetStatus', e.target.value)}>
              <option value="active">Ativo</option>
              <option value="maintenance">Em manutenção</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
          <div className="field">
            <label>Cor</label>
            <input className="input" value={form.color} onChange={e => set('color', e.target.value)} />
          </div>
          <div className="field">
            <label>Condutor</label>
            <input className="input" value={form.driverName} onChange={e => set('driverName', e.target.value)} />
          </div>
          <div className="field">
            <label>Km atual</label>
            <input type="number" inputMode="numeric" {...ptBrValidityProps} className="input" value={form.currentKm} onChange={e => set('currentKm', e.target.value)} />
          </div>
          <div className="field">
            <label>Próx. preventiva (km)</label>
            <input type="number" inputMode="numeric" {...ptBrValidityProps} className="input" value={form.nextPreventiveKm} onChange={e => set('nextPreventiveKm', e.target.value)} />
          </div>
          <div className="field">
            <label>Valor estimado (R$)</label>
            <input type="number" inputMode="decimal" step="0.01" {...ptBrValidityProps} className="input" value={form.estimatedValue} onChange={e => set('estimatedValue', e.target.value)} />
          </div>
          <div className="field">
            <label>Uso</label>
            <input className="input" value={form.usageLabel} onChange={e => set('usageLabel', e.target.value)} />
          </div>
        </div>
      </form>
    </FormPage>
  );
};

export default FrotaNovo;
